// Client-side photo treatments.
// - surveillanceFilter: amber-duotone avatar fallback (generative lab offline).
// - noirProof: dark-noir film treatment applied to every proof/claim selfie
//   before upload — high-contrast B/W with a whisper of warmth, film grain,
//   heavy vignette, and a typed timestamp. All pixel-loop, no ctx.filter
//   (Safari-safe).

/** Dark-noir treatment for proof selfies. Accepts any image blob. */
export function noirProof(source: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 1280;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas unavailable"));
      ctx.drawImage(img, 0, 0, w, h);

      const data = ctx.getImageData(0, 0, w, h);
      const px = data.data;
      const contrast = 1.45;
      const cx = w / 2, cy = h / 2;
      const maxD = Math.sqrt(cx * cx + cy * cy);
      for (let i = 0; i < px.length; i += 4) {
        const x = (i / 4) % w, y = Math.floor(i / 4 / w);
        let lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        lum = (lum - 118) * contrast + 118; // crush shadows a touch
        // film grain
        lum += (Math.random() - 0.5) * 22;
        // vignette
        const d = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) / maxD;
        lum *= 1 - 0.45 * d * d;
        const v = Math.min(255, Math.max(0, lum));
        // near-monochrome with a whisper of warmth in the highlights
        px[i] = v;
        px[i + 1] = v * 0.97;
        px[i + 2] = v * 0.9;
      }
      ctx.putImageData(data, 0, 0);

      // typed timestamp, bottom-left, like surveillance film
      const fs = Math.max(11, Math.round(w * 0.028));
      ctx.font = `${fs}px "Space Mono", monospace`;
      ctx.fillStyle = "rgba(255,196,90,0.85)";
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      ctx.fillText(
        `RVG ${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`,
        Math.round(w * 0.04),
        h - Math.round(w * 0.04),
      );

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Encode failed"))),
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image load failed"));
    };
    img.src = url;
  });
}

export function surveillanceFilter(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 1024;
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas unavailable"));
      ctx.drawImage(img, 0, 0, w, h);

      const data = ctx.getImageData(0, 0, w, h);
      const px = data.data;
      const contrast = 1.35;
      // Amber duotone endpoints (dark -> amber highlight).
      const lowR = 8, lowG = 10, lowB = 12;
      const hiR = 255, hiG = 176, hiB = 32;
      for (let i = 0; i < px.length; i += 4) {
        // grayscale (luma)
        let lum = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        // contrast around midpoint
        lum = (lum - 128) * contrast + 128;
        const t = Math.min(1, Math.max(0, lum / 255));
        px[i] = lowR + (hiR - lowR) * t;
        px[i + 1] = lowG + (hiG - lowG) * t;
        px[i + 2] = lowB + (hiB - lowB) * t;
      }
      ctx.putImageData(data, 0, 0);

      // Corner brackets overlay.
      ctx.strokeStyle = "rgba(255,176,32,0.85)";
      ctx.lineWidth = Math.max(2, Math.round(w * 0.006));
      const m = Math.round(Math.min(w, h) * 0.06);
      const len = Math.round(Math.min(w, h) * 0.12);
      const corner = (x: number, y: number, dx: number, dy: number) => {
        ctx.beginPath();
        ctx.moveTo(x, y + dy * len);
        ctx.lineTo(x, y);
        ctx.lineTo(x + dx * len, y);
        ctx.stroke();
      };
      corner(m, m, 1, 1);
      corner(w - m, m, -1, 1);
      corner(m, h - m, 1, -1);
      corner(w - m, h - m, -1, -1);

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Encode failed"))),
        "image/jpeg",
        0.85,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image load failed"));
    };
    img.src = url;
  });
}
